import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getScriptFormats } from './api';

// The script format and narration rate a class will be BUILT in.
//
// The format list comes from the server registry rather than a constant here, so adding
// a format does not need a frontend change to become selectable.
//
// The budget line is the same arithmetic the generator is given (words = minutes x rate,
// minus the clock time knowledge checks consume), shown before anyone spends a model
// call on the wrong length.
export default function ScriptFormatPicker({
  value, onChange, minutes, currentFormat, hasArtifacts,
  // Present only where changing the length is allowed: while the class is still in
  // development and nobody has bought it at the current length.
  delivery, onLengthChange, lengthChanging, lengthLocked, lengthLockReason,
}) {
  const { data } = useQuery({ queryKey: ['script-formats'], queryFn: getScriptFormats });
  const formats = data?.formats || [];
  const lengths = data?.class_lengths?.[delivery === 'live' ? 'live' : 'elearning'] || [];
  const format = value.script_format || currentFormat || data?.default || 'narrative';
  const wpm = Number(value.narration_wpm || data?.narration_wpm || 145);
  const chaptered = formats.find((f) => f.id === format)?.chaptered;

  // A rate of 0 (an ordinary intermediate keystroke) must not take the panel down.
  const words = minutes > 0 && wpm > 0 ? Math.floor(minutes * wpm) : null;
  const switching = hasArtifacts && currentFormat && format !== currentFormat;

  const field = 'text-xs bg-[#0b0e13] border border-[#2b3a4d] rounded px-2 py-1 text-[#c7d0e0]';

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-[10px] uppercase tracking-wide text-[#7d8aa0]">
          Script format
          <select className={`${field} block mt-1`} value={format}
            onChange={(e) => onChange({ ...value, script_format: e.target.value })}>
            {formats.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
        </label>

        {/* The class length drives the whole budget, so it is changeable here rather than
            only back on the concept: rebuilding a 60 minute class as a 120 minute one is
            the reason someone opens this control. */}
        <label className="text-[10px] uppercase tracking-wide text-[#7d8aa0]">
          Class length
          {onLengthChange && !lengthLocked ? (
            <select className={`${field} block mt-1`} value={minutes || ''}
              disabled={lengthChanging}
              onChange={(e) => onLengthChange(Number(e.target.value))}>
              <option value="">Not set</option>
              {lengths.map((m) => (
                <option key={m} value={m}>
                  {m >= 60 && m % 60 === 0 ? `${m / 60} hour${m === 60 ? '' : 's'}` : `${m} min`}
                  {' '}({m} min)
                </option>
              ))}
            </select>
          ) : (
            <span className="block mt-1 text-xs normal-case text-[#c7d0e0]">
              {minutes ? `${minutes} minutes` : 'not set'}
              {lengthLocked && (
                <span className="block text-[10px] text-[#5f6b7e]" title={lengthLockReason}>
                  fixed at this stage
                </span>
              )}
            </span>
          )}
        </label>

        {/* Both formats are narration for a class of the same scheduled length, so both
            are written to the budget. Only the STRUCTURE differs between them. */}
        <label className="text-[10px] uppercase tracking-wide text-[#7d8aa0]">
          Narration rate
          <span className="flex items-center gap-1 mt-1">
            <input type="number" min="60" max="300" className={`${field} w-20`} value={wpm}
              onChange={(e) => onChange({ ...value, narration_wpm: e.target.value })} />
            <span className="text-[10px] normal-case text-[#5f6b7e]">wpm</span>
          </span>
        </label>
      </div>

      <p className="text-[11px] text-[#8a95a8]">
        {words
          ? <>A {minutes} minute class at {wpm} wpm is about {words.toLocaleString()} words of
              narration, before the time knowledge checks take.
              {chaptered
                ? ' JPRO splits that across chapters and writes each one to its own target.'
                : ' JPRO writes the script to that length.'}</>
          : <>{minutes > 0
                ? 'Enter a narration rate to see the word budget.'
                : 'This class has no agreed length yet, so JPRO will write it without a word target. Set a length to budget the narration.'}</>}
      </p>

      {onLengthChange && !lengthLocked && (
        <p className="text-[11px] text-[#8a95a8]">
          Changing the length re-splits the budget. Rebuild the script afterwards for the
          chapters to be written to the new one.
        </p>
      )}

      {switching && (
        <p className="text-[11px] text-[#f7a23b]">
          Changing the format rewrites the script from scratch. The current one is replaced.
        </p>
      )}
    </div>
  );
}
